import { useMemo } from "react";
import { useAuth, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";

export interface ChartDataPoint {
  date: string;
  email: number;
  chat: number;
  sms: number;
}

export interface TicketChartDataPoint {
  date: string;
  opened: number;
  completed: number;
}

export interface ContactChartDataPoint {
  date: string;
  added: number;
  removed: number;
}

/**
 * Hook to generate chart data from real application data
 */
export function useDashboardChartData() {
  const { currentUser } = useAuth();
  const inboxThreadsQuery = useTellescopeInfiniteQuery({
    model: "inbox_threads",
    method: "load_threads",
    request: {
      autobuild: true,
    },
    getNextPageParam: (lastPage) => lastPage.threads.at(-1)?.timestamp,
    initialPageParam: undefined,
    paginateRequest: (lastTimestamp, request) => ({
      ...request,
      lastTimestamp,
    }),
    select: (data) => data.pages.map(({ threads }) => threads).flat(),
    enabled: !!currentUser.id,
  });
  const ticketsQuery = useTellescopeInfiniteQuery({
    model: "tickets",
    method: "getSome",
    request: {
      filter: {
        owner: currentUser.id,
      },
    },
    enabled: !!currentUser.id,
    select: (data) => data.pages.flat(),
  });
  const endusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {},
    enabled: !!currentUser.id,
    select: (data) => data.pages.flat(),
  });

  // Generate message chart data from inbox threads
  const messageChartData = useMemo(() => {
    const threads = inboxThreadsQuery.data || [];
    const dataMap = new Map<string, { email: number; chat: number; sms: number }>();

    // Group by date and count by type
    threads.forEach((thread) => {
      const date = new Date(thread.timestamp).toISOString().split("T")[0] ?? "";
      const existing = dataMap.get(date) || { email: 0, chat: 0, sms: 0 };

      if (thread.type === "Email") existing.email++;
      else if (thread.type === "Chat") existing.chat++;
      else if (thread.type === "SMS") existing.sms++;

      dataMap.set(date, existing);
    });

    // Convert to array and sort by date
    const chartData: ChartDataPoint[] = Array.from(dataMap.entries())
      .map(([date, counts]) => ({ date, ...counts }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Fill in missing dates with zero counts for the last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    const filledData: ChartDataPoint[] = [];

    for (let d = new Date(thirtyDaysAgo); d <= today; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split("T")[0] ?? "";
      const existing = chartData.find((item) => item.date === dateStr);
      filledData.push(existing || { date: dateStr, email: 0, chat: 0, sms: 0 });
    }

    return filledData;
  }, [inboxThreadsQuery.data]);

  // Generate ticket chart data
  const ticketChartData = useMemo(() => {
    const tickets = ticketsQuery.data || [];
    const dataMap = new Map<string, { opened: number; completed: number }>();

    tickets.forEach((ticket) => {
      // Count by creation date (opened)
      const openedDate = new Date(ticket.createdAt).toISOString().split("T")[0] ?? "";
      const openedData = dataMap.get(openedDate) || { opened: 0, completed: 0 };
      openedData.opened++;
      dataMap.set(openedDate, openedData);

      // Count by completion date if ticket is completed
      if (ticket.closedAt) {
        const completedDate = new Date(ticket.closedAt).toISOString().split("T")[0] ?? "";
        const completedData = dataMap.get(completedDate) || { opened: 0, completed: 0 };
        completedData.completed++;
        dataMap.set(completedDate, completedData);
      }
    });

    // Convert to array and sort
    const chartData: TicketChartDataPoint[] = Array.from(dataMap.entries())
      .map(([date, counts]) => ({ date, ...counts }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Fill in last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    const filledData: TicketChartDataPoint[] = [];

    for (let d = new Date(thirtyDaysAgo); d <= today; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split("T")[0] ?? "";
      const existing = chartData.find((item) => item.date === dateStr);
      filledData.push(existing || { date: dateStr, opened: 0, completed: 0 });
    }

    return filledData;
  }, [ticketsQuery.data]);

  // Generate contact chart data
  const contactChartData = useMemo(() => {
    const contacts = endusersQuery.data || [];
    const dataMap = new Map<string, { added: number; removed: number }>();

    // Group by creation date (we don't have removal data, so removed will be 0)
    contacts.forEach((contact) => {
      const date = new Date(contact.createdAt).toISOString().split("T")[0] ?? "";
      const existing = dataMap.get(date) || { added: 0, removed: 0 };
      existing.added++;
      dataMap.set(date, existing);
    });

    // Convert to array and sort
    const chartData: ContactChartDataPoint[] = Array.from(dataMap.entries())
      .map(([date, counts]) => ({ date, ...counts }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Fill in last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    const filledData: ContactChartDataPoint[] = [];

    for (let d = new Date(thirtyDaysAgo); d <= today; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split("T")[0] ?? "";
      const existing = chartData.find((item) => item.date === dateStr);
      filledData.push(existing || { date: dateStr, added: 0, removed: 0 });
    }

    return filledData;
  }, [endusersQuery.data]);

  return {
    messageChartData,
    ticketChartData,
    contactChartData,
    isLoading: inboxThreadsQuery.isLoading || ticketsQuery.isLoading || endusersQuery.isLoading,
  };
}
